import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from 'layouts/AppLayout';

import { FileInput } from 'components/FileInput';
import { ProfileModal } from 'components/modals/ProfileModal';
import { FeedbackModal } from 'components/modals/FeedbackModal';
import { useApp } from 'context/AppContext';
import { generateUniqueId } from 'utils/id';
import { filterEntries } from 'utils/entries';
import { downloadEirFile, downloadTxtFile, createEirYamlSnapshot } from 'utils/export';
import { AppPage, EIRData, ViewMode, ChatJournalEntry, RemoteEhrSession, NutritionLogEntry, HealthAction, ForYouItem, ForYouItemType, ChatThread, HealthAnalysis } from 'types';
import { apiService } from 'services/api';
import { restoreHealthMemoryFromImport, healthMemoryService } from 'services/healthMemory';
import { BankIdModal } from 'components/modals/BankIdModal';
import { EirLoadOptionsModal } from 'components/modals/EirLoadOptionsModal';
import { CONFIG } from 'config';
import { PageRoutes } from 'pages/PageRoutes';
import { getPathForPage, resolvePageFromPath } from 'utils/navigation';
import { applyPageMetadata, resolvePageMetadata } from 'utils/seo';
import { mergeById } from 'utils/collections';
import { getErrorMessage } from 'utils/errors';
import { OnboardingOverlay } from 'features/Onboarding';
import { OnboardingProvider, useOnboarding } from 'context/OnboardingContext';
import { PersonalizedOnboarding } from 'features/Onboarding';
import {
  FeatureRecommendation,
  OnboardingAnswer,
  PERSONALIZED_ONBOARDING_STORAGE_KEY,
  ONBOARDING_ANSWERS_STORAGE_KEY,
  ONBOARDING_RECOMMENDATIONS_STORAGE_KEY,
  calculateRecommendations,
} from 'utils/onboardingRecommendations';
import { useAuth } from 'context/AuthContext';
import { cloudSyncService, hashString } from 'services/cloudSync';
import { supabaseAuth } from 'services/supabaseAuth';
import { applyTheme, DEFAULT_THEME_ID, isValidThemeId, getStoredThemeMode, isThemeDark, type ThemeId, type ThemeMode } from 'theme';
import { RemoteSessionIndicator } from 'components/RemoteSessionIndicator';
import { ASSESSMENT_COMPLETE_EVENT, TRIGGER_HEALTH_ANALYSIS_EVENT, TRIGGER_CHAT_WITH_AI_EVENT } from 'constants/events';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { persistence } from 'services/persistence';
import { GlobalSearch, SearchResult } from 'components/GlobalSearch';
import { usePageTracking } from 'hooks/usePageTracking';
import { useCloudSyncLifecycle } from 'hooks/useCloudSyncLifecycle';
import { useEventListener } from 'hooks/useEventListener';
import { useAsyncEffect } from 'hooks/useAsyncEffectGuard';
import { useLocalStorageCount } from 'hooks/useLocalStorageCount';
import { useChatTransfer } from 'hooks/useChatTransfer';
import { useOnboardingFlow } from 'hooks/useOnboardingFlow';
import { useRemoteEhrPolling } from 'hooks/useRemoteEhrPolling';
import { triggerFileInput } from 'utils/dom';

import {
  JOURNAL_STORAGE_KEY,
  CHAT_JOURNAL_STORAGE_KEY,
  EIR_FILE_STORAGE_KEY,
  EIR_DATA_STORAGE_KEY,
  HEALTH_ANALYSIS_STORAGE_KEY,
  HEALTH_ANALYSIS_META_STORAGE_KEY,
  CALENDAR_EVENTS_STORAGE_KEY,
  CALENDAR_SUGGESTIONS_STORAGE_KEY,
  clampScore,
  formatNoteTitle,
  normalizeJournalNotes,
  normalizeExportedNotes,
  normalizeCalendarEvents,
  normalizeChatJournalEntries,
  parseEirText
} from 'utils/appHelpers';

const updateFavicon = (themeId: ThemeId, mode: ThemeMode) => {
  const src = isThemeDark(themeId, mode) ? '/eir-logo-dark-bg.svg' : '/eir-logo-light-bg.svg';
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link) link.href = src;
  const appleLink = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (appleLink) appleLink.href = src;
};

export const App: React.FC = () => {
  const handleOnboardingComplete = useCallback(() => {
    // This will be handled by the inner component
  }, []);

  return (
    <OnboardingProvider onComplete={handleOnboardingComplete} onFetchFrom1177={() => {}}>
      <AppWithPersonalizedOnboarding />
    </OnboardingProvider>
  );
};

// Inner component that can use onboarding context
const AppWithPersonalizedOnboarding: React.FC = () => {
  const { hasCompletedPersonalized } = useOnboarding();
  const [showPersonalizedOnboarding, setShowPersonalizedOnboarding] = useState(false);

  // Check if personalized onboarding should be shown
  useEffect(() => {
    // Check for manual trigger from profile
    const triggerFlag = persistence.getItem(STORAGE_KEYS.TRIGGER_ONBOARDING) === 'true';
    if (triggerFlag) {
      persistence.removeItem(STORAGE_KEYS.TRIGGER_ONBOARDING);
      setShowPersonalizedOnboarding(true);
      return;
    }
    
    // Auto-show for new users
    const completed = persistence.getItem(PERSONALIZED_ONBOARDING_STORAGE_KEY) === 'true';
    if (!completed && !hasCompletedPersonalized) {
      // Small delay to ensure app is rendered
      const timer = setTimeout(() => {
        setShowPersonalizedOnboarding(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [hasCompletedPersonalized]);
  
  // Listen for trigger event
  useEventListener('eir-trigger-personalized-onboarding', () => {
    setShowPersonalizedOnboarding(true);
  });

  const handlePersonalizedOnboardingComplete = useCallback((
    recommendations: FeatureRecommendation[],
    answers: OnboardingAnswer
  ) => {
    // Store answers and recommendations
    persistence.setItem(PERSONALIZED_ONBOARDING_STORAGE_KEY, 'true');
    persistence.setJSON(ONBOARDING_ANSWERS_STORAGE_KEY, answers);
    persistence.setJSON(ONBOARDING_RECOMMENDATIONS_STORAGE_KEY, recommendations);
    
    // Store flag to trigger post-onboarding actions in AppContent
    persistence.setJSON(STORAGE_KEYS.ONBOARDING_PENDING, {
      recommendations,
      answers
    });
    
    setShowPersonalizedOnboarding(false);
    
    // Small delay before navigation to ensure localStorage is set and AppContent can detect it
    // Also trigger a custom event to ensure AppContent processes it
    setTimeout(() => {
      // Trigger custom event to notify AppContent
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('eir-onboarding-complete'));
      }
      // AppContent will handle creating the chat thread and navigating
    }, 150);
  }, []);

  const handlePersonalizedOnboardingSkip = useCallback(() => {
    // Use default recommendations
    const defaultAnswers: OnboardingAnswer = {
      primaryGoal: ['improve-wellbeing'],
      currentSituation: ['new-tracking'],
      immediateNeeds: ['take-assessment']
    };
    // Calculate default recommendations
    const defaultRecommendations = calculateRecommendations(defaultAnswers);
    handlePersonalizedOnboardingComplete(defaultRecommendations, defaultAnswers);
  }, [handlePersonalizedOnboardingComplete]);

  return (
    <>
      <AppContent />
      <PersonalizedOnboarding
        isOpen={showPersonalizedOnboarding}
        onComplete={handlePersonalizedOnboardingComplete}
        onSkip={handlePersonalizedOnboardingSkip}
      />
    </>
  );
};

// Main app content component
const AppContent: React.FC = () => {
  const { state, dispatch } = useApp();
  const { user: authUser, session: authSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track page visits and time spent
  usePageTracking();
  const [showEirLoadOptionsModal, setShowEirLoadOptionsModal] = useState(false);
  const [remoteEhrSession, setRemoteEhrSession] = useState<RemoteEhrSession | null>(null);
  const [isBankIdModalOpen, setIsBankIdModalOpen] = useState(false);
  const [remoteEhrError, setRemoteEhrError] = useState<string | null>(null);
  const [isStartingRemoteEhr, setIsStartingRemoteEhr] = useState(false);
  const [isFetchingRemoteEhrData, setIsFetchingRemoteEhrData] = useState(false);
  const [showRemoteSessionIndicator, setShowRemoteSessionIndicator] = useState(false);
  const [storedTxtContent, setStoredTxtContent] = useState<string | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const healthAssessmentCount = useLocalStorageCount<Record<string, unknown> | null>(
    STORAGE_KEYS.HEALTH_ASSESSMENT_RESULTS,
    null,
    (results) => {
      if (!results || Array.isArray(results)) return 0;
      return Object.values(results).filter((result: unknown) => {
        if (!result || typeof result !== 'object') return false;
        const r = result as Record<string, unknown>;
        return (r.overall && typeof r.overall === 'object' && typeof (r.overall as Record<string, unknown>).score === 'number') ||
               (Array.isArray(r.dimensions) && r.dimensions.length > 0);
      }).length;
    },
  );

  const activeHealthActionsCount = useLocalStorageCount<HealthAction[]>(
    STORAGE_KEYS.HEALTH_ACTIONS,
    [],
    (actions) => actions.filter(action => !action.completed).length,
  );

  const nutritionCount = useLocalStorageCount<NutritionLogEntry[]>(
    'eir-nutrition-meal-log',
    [],
    (entries) => entries.length,
  );
  const [betaFeaturesEnabled, setBetaFeaturesEnabled] = useState(false);
  const [cloudSyncEnabled, setCloudSyncEnabled] = useState(false);
  const remoteEhrFetchedRef = useRef(false);
  const remoteEhrSessionRef = useRef<RemoteEhrSession | null>(null);
  const completionIndicatorShownRef = useRef(false);
  const hasAttemptedEirRestoreRef = useRef(false);
  const healthAnalysisLoadedRef = useRef(false);
  const chatJournalLoadedRef = useRef(false);
  const greetingSentRef = useRef<string | null>(null); // Track if greeting was sent for current data

  useEventListener(ASSESSMENT_COMPLETE_EVENT, () => {
    if (state.data) {
      setShowEirLoadOptionsModal(true);
    }
  });

  const refreshBetaPreference = useCallback(() => {
    const parsedProfile = persistence.getJSON<Record<string, unknown> | null>('eir-user-profile', null);
    if (!parsedProfile) {
      const defaultMode = getStoredThemeMode();
      applyTheme(DEFAULT_THEME_ID, defaultMode);
      updateFavicon(DEFAULT_THEME_ID, defaultMode);
      setBetaFeaturesEnabled(false);
      setCloudSyncEnabled(false);
      return;
    }

    const themeId = isValidThemeId(parsedProfile?.theme) ? (parsedProfile.theme as ThemeId) : DEFAULT_THEME_ID;
    const mode = getStoredThemeMode();
    applyTheme(themeId, mode);
    updateFavicon(themeId, mode);
    setBetaFeaturesEnabled(Boolean(parsedProfile?.betaFeaturesEnabled));
    setCloudSyncEnabled(Boolean(parsedProfile?.cloudSyncEnabled) && Boolean(authUser));
  }, [authUser]);


  useEffect(() => {
    refreshBetaPreference();
  }, [refreshBetaPreference]);

  useEventListener('storage', (event: StorageEvent) => {
    if (event.key === 'eir-user-profile') {
      refreshBetaPreference();
    }
  });

  useEventListener('profileUpdated', () => {
    refreshBetaPreference();
  });

  useEffect(() => {
    if (!authUser) {
      setCloudSyncEnabled(false);
    } else {
      refreshBetaPreference();
    }
  }, [authUser, refreshBetaPreference]);

  useCloudSyncLifecycle({
    authUser,
    cloudSyncEnabled,
    state: {
      data: state.data,
      journalNotes: state.journalNotes,
      chatJournalEntries: state.chatJournalEntries,
      healthAnalysis: state.healthAnalysis,
      healthAnalysisGeneratedAt: state.healthAnalysisGeneratedAt,
    },
  });

  useEffect(() => {
    if (state.data) {
      const filtered = filterEntries(state.data.entries, state.filters);
      dispatch({ type: 'SET_FILTERED_ENTRIES', payload: filtered });
    }
  }, [state.data, state.filters, dispatch]);

  useChatTransfer();

  useEffect(() => {
    remoteEhrSessionRef.current = remoteEhrSession;
  }, [remoteEhrSession]);

  useEffect(() => {
    return () => {
      const activeSession = remoteEhrSessionRef.current;
      if (
        activeSession &&
        ['initializing', 'awaiting_bankid', 'authenticated'].includes(activeSession.status)
      ) {
        void apiService.cancelRemoteEhrSession(activeSession.sessionId).catch(() => undefined);
      }
    };
  }, []);

  // Listen for custom BankID trigger event from skill widgets
  // Note: We need to use a ref to access the latest handleRemoteEhrStart function
  const handleRemoteEhrStartRef = useRef<(() => Promise<void>) | undefined>(undefined);
  
  useEffect(() => {
    handleRemoteEhrStartRef.current = handleRemoteEhrStart;
  });

  useEventListener('eir:trigger-bankid', () => {
    console.log('[App] Received eir:trigger-bankid event, starting remote EHR session');
    if (handleRemoteEhrStartRef.current) {
      handleRemoteEhrStartRef.current();
    }
  });

  useEffect(() => {
    const parsed = persistence.getJSON<unknown[]>(JOURNAL_STORAGE_KEY, []);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const normalized = normalizeJournalNotes(parsed);
      dispatch({ type: 'SET_JOURNAL_NOTES', payload: normalized });
    }
  }, [dispatch]);

  useEffect(() => {
    persistence.setJSON(JOURNAL_STORAGE_KEY, state.journalNotes);
  }, [state.journalNotes]);

  useEffect(() => {
    const parsedEvents = persistence.getJSON<unknown[]>(CALENDAR_EVENTS_STORAGE_KEY, []);
    if (Array.isArray(parsedEvents) && parsedEvents.length > 0) {
      dispatch({ type: 'SET_CALENDAR_EVENTS', payload: normalizeCalendarEvents(parsedEvents) });
    }

    const parsedSuggestions = persistence.getJSON<unknown[]>(CALENDAR_SUGGESTIONS_STORAGE_KEY, []);
    if (Array.isArray(parsedSuggestions)) {
      const normalized = parsedSuggestions
        .filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0);
      if (normalized.length > 0) {
        dispatch({ type: 'SET_CALENDAR_SUGGESTIONS', payload: normalized });
      }
    }
  }, [dispatch]);

  useEffect(() => {
    persistence.setJSON(CALENDAR_EVENTS_STORAGE_KEY, state.calendarEvents);
  }, [state.calendarEvents]);

  useEffect(() => {
    persistence.setJSON(CALENDAR_SUGGESTIONS_STORAGE_KEY, state.calendarSuggestions);
  }, [state.calendarSuggestions]);

  // Load chat journal entries from localStorage (only on mount)
  useEffect(() => {
    if (chatJournalLoadedRef.current) return;
    chatJournalLoadedRef.current = true;

    const parsed = persistence.getJSON<unknown[]>(CHAT_JOURNAL_STORAGE_KEY, []);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const normalized = normalizeChatJournalEntries(parsed);
      dispatch({ type: 'SET_CHAT_JOURNAL_ENTRIES', payload: normalized });
      console.log('[App] Restored chat journal entries from localStorage:', normalized.length);
    }
  }, [dispatch]);

  // Save chat journal entries to localStorage
  // Only save after initial load to prevent overwriting with empty array
  useEffect(() => {
    if (!chatJournalLoadedRef.current) return;
    persistence.setJSON(CHAT_JOURNAL_STORAGE_KEY, state.chatJournalEntries);
  }, [state.chatJournalEntries]);

  // Load health analysis from localStorage (only on mount)
  useEffect(() => {
    if (healthAnalysisLoadedRef.current) return;
    healthAnalysisLoadedRef.current = true;
    
    const analysis = persistence.getJSON<HealthAnalysis | null>(HEALTH_ANALYSIS_STORAGE_KEY, null);
    if (analysis !== null) {
      const meta = persistence.getJSON<{ generatedAt?: string; model?: string }>(HEALTH_ANALYSIS_META_STORAGE_KEY, {});
      dispatch({
        type: 'SET_HEALTH_ANALYSIS',
        payload: {
          analysis,
          generatedAt: meta.generatedAt,
          model: meta.model
        }
      });
      console.log('[App] Restored health analysis from localStorage');
    }
  }, [dispatch]);

  // Save health analysis to localStorage
  // Only save, never remove (let user explicitly delete if needed)
  useEffect(() => {
    if (state.healthAnalysis) {
      persistence.setJSON(HEALTH_ANALYSIS_STORAGE_KEY, state.healthAnalysis);
      persistence.setJSON(HEALTH_ANALYSIS_META_STORAGE_KEY, {
        generatedAt: state.healthAnalysisGeneratedAt,
        model: state.healthAnalysisModel
      });
      console.log('[App] Saved health analysis to localStorage');
    }
    // Don't remove on null/undefined - preserve existing data
  }, [state.healthAnalysis, state.healthAnalysisGeneratedAt, state.healthAnalysisModel]);

  // Save EIR data structure to localStorage
  useEffect(() => {
    // Don't touch localStorage until we actually have state data to persist.
    // When the app first mounts we want the hydration effect below to read the
    // existing value instead of having it cleared immediately.
    if (!state.data) {
      return;
    }

    if (Array.isArray(state.data.entries) && state.data.entries.length > 0) {
      persistence.setJSON(EIR_DATA_STORAGE_KEY, state.data);
    } else {
      persistence.removeItem(EIR_DATA_STORAGE_KEY);
    }
  }, [state.data]);

  // Load EIR file from localStorage on mount if not already loaded
  useEffect(() => {
    if (hasAttemptedEirRestoreRef.current || state.data) {
      return; // Skip if already attempted restore or already have data
    }

    hasAttemptedEirRestoreRef.current = true;
    
    // First try to load parsed EIR data (faster)
    const parsedData = persistence.getJSON<EIRData | null>(EIR_DATA_STORAGE_KEY, null);
    if (parsedData && parsedData.entries && Array.isArray(parsedData.entries)) {
      dispatch({ type: 'RESET_HEALTH_ANALYSIS' });
      dispatch({ type: 'SET_DATA', payload: parsedData });
      dispatch({ type: 'SET_ACTIVE_PAGE', payload: 'ehr' });
      greetingSentRef.current = null;
      console.log('[App] Restored EIR data from localStorage');
      return;
    }

    // Fallback to loading from file content (text/YAML)
    const storedEirContent = persistence.getItem(EIR_FILE_STORAGE_KEY);
    if (!storedEirContent) {
      return;
    }

    try {
      const { data, notes } = parseEirText(storedEirContent);
      dispatch({ type: 'RESET_HEALTH_ANALYSIS' });
      dispatch({ type: 'SET_DATA', payload: data });
      dispatch({ type: 'SET_JOURNAL_NOTES', payload: notes });
      dispatch({ type: 'SET_ACTIVE_PAGE', payload: 'ehr' });
      greetingSentRef.current = null;
      console.log('[App] Restored EIR file from localStorage');
    } catch (error) {
      console.error('Error loading stored EIR file:', error);
      persistence.removeItem(EIR_FILE_STORAGE_KEY);
      persistence.removeItem(EIR_DATA_STORAGE_KEY);
    }
  }, [dispatch, state.data]); // Run once on mount or if data is cleared

  // Removed automatic sample file loading - users should load their own EIR files


  // Detect if device is mobile
  const isMobileDevice = typeof window !== 'undefined' && (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ||
    ('ontouchstart' in window || navigator.maxTouchPoints > 0)
  );

  useRemoteEhrPolling({
    remoteEhrSession,
    setRemoteEhrSession,
    isMobileDevice,
    dispatch,
    setRemoteEhrError,
    setIsBankIdModalOpen,
    setIsFetchingRemoteEhrData,
    setStoredTxtContent,
    setShowEirLoadOptionsModal,
    remoteEhrFetchedRef,
    greetingSentRef,
  });

  useEffect(() => {
    if (!remoteEhrSession || remoteEhrSession.status !== 'authenticated' || !isBankIdModalOpen) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setIsBankIdModalOpen(false);
    }, 1200);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [remoteEhrSession, isBankIdModalOpen]);

  useEffect(() => {
    if (isBankIdModalOpen) {
      setShowRemoteSessionIndicator(false);
      return;
    }

    if (!remoteEhrSession) {
      setShowRemoteSessionIndicator(false);
      completionIndicatorShownRef.current = false;
      return;
    }

    if (remoteEhrSession.status === 'authenticated' || isFetchingRemoteEhrData) {
      completionIndicatorShownRef.current = false;
      if (!showRemoteSessionIndicator) {
        setShowRemoteSessionIndicator(true);
      }
      return;
    }

    if (remoteEhrSession.status === 'completed' && !isFetchingRemoteEhrData) {
      if (!completionIndicatorShownRef.current) {
        completionIndicatorShownRef.current = true;
        setShowRemoteSessionIndicator(true);
        const timeout = window.setTimeout(() => {
          setShowRemoteSessionIndicator(false);
        }, 3200);

        return () => {
          window.clearTimeout(timeout);
        };
      }

      return;
    }

    if (remoteEhrSession.status === 'failed' || remoteEhrSession.status === 'cancelled') {
      setShowRemoteSessionIndicator(false);
      completionIndicatorShownRef.current = false;
    }
  }, [remoteEhrSession, isFetchingRemoteEhrData, isBankIdModalOpen, showRemoteSessionIndicator]);

  const loadEirFromText = useCallback(async (text: string, source: 'file' | 'cloud' = 'file') => {
    try {
      const { data, notes, chatEntries, healthAssessments, nutritionEntries, healthActions, healthMemoryItems } = parseEirText(text);

      // Store the file content in localStorage
      persistence.setItem(EIR_FILE_STORAGE_KEY, text);

      dispatch({ type: 'RESET_HEALTH_ANALYSIS' });
      dispatch({ type: 'SET_DATA', payload: data });
      dispatch({ type: 'SET_JOURNAL_NOTES', payload: notes });
      
      // Load extended entry types if present in the .eir file
      {
        // Load chat entries (EIR Journal)
        if (chatEntries && chatEntries.length > 0) {
          const existing = persistence.getJSON<ChatJournalEntry[]>(CHAT_JOURNAL_STORAGE_KEY, []);
          const merged = mergeById(existing, chatEntries);
          persistence.setJSON(CHAT_JOURNAL_STORAGE_KEY, merged);
          dispatch({ type: 'SET_CHAT_JOURNAL_ENTRIES', payload: merged });
        }
        
        // Load health assessments
        if (healthAssessments) {
          console.log('[App] Loading health assessments from', source, ':', {
            hasResponses: !!healthAssessments.responses,
            hasResults: !!healthAssessments.results,
            responsesType: typeof healthAssessments.responses,
            resultsType: typeof healthAssessments.results
          });
          
          if (healthAssessments.responses && typeof healthAssessments.responses === 'object') {
            const responsesObj = healthAssessments.responses;
            const existingResponses = persistence.getJSON<Record<string, unknown>>(STORAGE_KEYS.HEALTH_ASSESSMENT_RESPONSES, {});
            const mergedResponses = { ...existingResponses, ...responsesObj };
            persistence.setJSON(STORAGE_KEYS.HEALTH_ASSESSMENT_RESPONSES, mergedResponses);
            console.log('[App] Stored health assessment responses:', Object.keys(mergedResponses).length, 'forms');
          }
          
          if (healthAssessments.results) {
            // Ensure results is properly structured - if any values are strings, try to parse them
            const processedResults: Record<string, any> = {};
            if (typeof healthAssessments.results === 'object' && !Array.isArray(healthAssessments.results)) {
              Object.entries(healthAssessments.results).forEach(([formId, result]) => {
                if (typeof result === 'string') {
                  // Try to parse if it's a JSON string
                  try {
                    processedResults[formId] = JSON.parse(result);
                  } catch (e) {
                    // If parsing fails, skip this result
                    console.warn(`[App] Failed to parse health assessment result for form ${formId}:`, e);
                  }
                } else if (result && typeof result === 'object') {
                  processedResults[formId] = result;
                }
              });
              
              if (Object.keys(processedResults).length > 0) {
                const existingResults = persistence.getJSON<Record<string, unknown>>(STORAGE_KEYS.HEALTH_ASSESSMENT_RESULTS, {});
                const mergedResults = { ...existingResults, ...processedResults };
                persistence.setJSON(STORAGE_KEYS.HEALTH_ASSESSMENT_RESULTS, mergedResults);
                console.log('[App] Stored health assessment results:', Object.keys(mergedResults).length, 'forms');
              } else {
                console.warn('[App] No valid health assessment results to store');
              }
            } else {
              console.warn('[App] Health assessment results is not a valid object:', typeof healthAssessments.results);
            }
          } else {
            console.log('[App] No health assessment results found in parsed data');
          }
        } else {
          console.log('[App] No health assessments found in parsed data');
        }
        
        // Load nutrition entries
        if (nutritionEntries && nutritionEntries.length > 0) {
          const existing = persistence.getJSON<NutritionLogEntry[]>('eir-nutrition-meal-log', []);
          const merged = mergeById(existing, nutritionEntries);
          persistence.setJSON('eir-nutrition-meal-log', merged);
        }
        
        // Load health actions
        if (healthActions && healthActions.length > 0) {
          const existing = persistence.getJSON<HealthAction[]>(STORAGE_KEYS.HEALTH_ACTIONS, []);
          const merged = mergeById(existing, healthActions);
          persistence.setJSON(STORAGE_KEYS.HEALTH_ACTIONS, merged);
        }

        // Load health memory items (merge when memory is enabled)
        await restoreHealthMemoryFromImport(healthMemoryItems);
      }
      
      dispatch({ type: 'SET_ACTIVE_PAGE', payload: 'ehr' });
      setShowEirLoadOptionsModal(data.entries.length > 0);
      
      // Reset greeting sent flag when new data is loaded
      greetingSentRef.current = null;
      
      if (source === 'cloud') {
        console.log('[App] Loaded EIR backup from cloud');
      }
      
      return true;
    } catch (error) {
      console.error(`Error loading EIR ${source === 'cloud' ? 'backup' : 'file'}:`, error);
      if (source === 'file') {
      dispatch({ type: 'SET_ERROR', payload: 'Error loading EIR file. Please check the file format.' });
      setShowEirLoadOptionsModal(false);
      }
      return false;
    }
  }, [dispatch]);

  const handleFileLoad = async (file: File) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const text = await file.text();
      await loadEirFromText(text, 'file');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Load EIR backup from cloud when user logs in
  const hasLoadedCloudBackupRef = useRef<string | null>(null);
  useAsyncEffect(async (isCancelled) => {
    if (!authUser || !supabaseAuth.isConfigured) {
      hasLoadedCloudBackupRef.current = null;
      return;
    }
    if (hasLoadedCloudBackupRef.current === authUser.id) return;
    if (state.data && state.data.entries && state.data.entries.length > 0) {
      console.log('[App] Skipping cloud backup load - user already has data loaded');
      hasLoadedCloudBackupRef.current = authUser.id;
      return;
    }

    try {
      console.log('[App] Loading EIR backup from cloud for user:', authUser.id);
      const backupContent = await cloudSyncService.fetchEirBackup(authUser.id, authSession);
      if (isCancelled()) return;

      if (backupContent) {
        console.log('[App] Found EIR backup in cloud, loading...');
        dispatch({ type: 'SET_LOADING', payload: true });
        const success = await loadEirFromText(backupContent, 'cloud');
        if (isCancelled()) return;
        if (success) {
          hasLoadedCloudBackupRef.current = authUser.id;
          console.log('[App] Successfully loaded EIR backup from cloud');
        }
      } else {
        console.log('[App] No EIR backup found in cloud');
        hasLoadedCloudBackupRef.current = authUser.id;
      }
    } catch (error) {
      console.warn('[App] Failed to load EIR backup from cloud:', error);
    } finally {
      if (!isCancelled()) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }, [authUser, authSession, loadEirFromText, dispatch, state.data]);

  // Listen for EIR file load events from drag-and-drop
  useEventListener('loadEirFileFromText', async (event: Event) => {
    const customEvent = event as CustomEvent<{ text: string; source: 'file' | 'cloud' }>;
    const { text, source } = customEvent.detail;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await loadEirFromText(text, source);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  });

  const resetRemoteEhrState = (options: { keepModalOpen?: boolean } = {}) => {
    if (!options.keepModalOpen) {
      setIsBankIdModalOpen(false);
    }

    setRemoteEhrSession(null);
    setIsFetchingRemoteEhrData(false);
    remoteEhrFetchedRef.current = false;
    completionIndicatorShownRef.current = false;
    setShowRemoteSessionIndicator(false);
    setRemoteEhrError(null);
    // Keep storedTxtContent - it persists for later export
  };

  const handleRemoteEhrStart = async () => {
    if (isStartingRemoteEhr) {
      return;
    }

    const activeStatus = remoteEhrSession?.status;
    const isSessionActive = activeStatus && ['initializing', 'awaiting_bankid', 'authenticated'].includes(activeStatus);

    setIsStartingRemoteEhr(true);
    setRemoteEhrError(null);
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      if (remoteEhrSession && isSessionActive) {
        await apiService.cancelRemoteEhrSession(remoteEhrSession.sessionId);
      }

      resetRemoteEhrState({ keepModalOpen: true });
      const session = await apiService.startRemoteEhrSession();
      const nextSession: RemoteEhrSession = {
        sessionId: session.sessionId,
        status: session.status,
        qrImage: session.qrImage ?? null,
        error: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      remoteEhrFetchedRef.current = false;
      setRemoteEhrSession(nextSession);
      // Only show modal on desktop - mobile uses deep link automatically
      if (!isMobileDevice) {
        setIsBankIdModalOpen(true);
      }
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to start BankID session.');
      setRemoteEhrError(message);
      dispatch({ type: 'SET_ERROR', payload: message });
    } finally {
      setIsStartingRemoteEhr(false);
    }
  };

  const handleRefreshBankIdQr = async () => {
    if (!remoteEhrSession) {
      return;
    }

    try {
      const { qrImage } = await apiService.getRemoteEhrQr(remoteEhrSession.sessionId);
      setRemoteEhrSession((previous) => (previous ? { ...previous, qrImage } : previous));
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to refresh BankID QR code.');
      setRemoteEhrError(message);
    }
  };

  const handleCancelRemoteEhrSession = async () => {
    if (remoteEhrSession && ['initializing', 'awaiting_bankid', 'authenticated'].includes(remoteEhrSession.status)) {
      try {
        await apiService.cancelRemoteEhrSession(remoteEhrSession.sessionId);
      } catch (error) {
        console.warn('Unable to cancel BankID session', error);
      }
    }

    resetRemoteEhrState();
    setIsStartingRemoteEhr(false);
  };

  const handleCloseBankIdModal = async () => {
    if (!remoteEhrSession) {
      setIsBankIdModalOpen(false);
      return;
    }

    const { status } = remoteEhrSession;

    if (['initializing', 'awaiting_bankid'].includes(status)) {
      await handleCancelRemoteEhrSession();
      return;
    }

    setIsBankIdModalOpen(false);

    if (status === 'authenticated' || isFetchingRemoteEhrData) {
      return;
    }

    if (status === 'completed' || status === 'failed' || status === 'cancelled') {
      resetRemoteEhrState({ keepModalOpen: true });
      setIsStartingRemoteEhr(false);
    }
  };

  const handleOpenRemoteSessionDetails = useCallback(() => {
    // Only show modal on desktop - mobile uses deep link automatically
    if (!isMobileDevice) {
      setIsBankIdModalOpen(true);
    }
  }, [isMobileDevice]);

  const handleDismissGlobalError = () => {
    setRemoteEhrError(null);
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const handleSubmitFeedback = async (feedback: { 
    rating: number; 
    message: string; 
    contactEmail?: string; 
    contactPhone?: string;
  }) => {
    try {
      await apiService.submitFeedback(feedback);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw error; // Re-throw so modal can handle it
    }
  };

  const handleExport = async () => {
    if (state.data) {
      // Export .eir file (always from current state)
      await downloadEirFile(state.data, state.journalNotes);
      
      // Export .txt file (use stored content from BankID if available, otherwise generate)
      if (storedTxtContent) {
        console.log('[Export] Using stored TXT content from BankID session');
        // Create a blob and download it
        const blob = new Blob([storedTxtContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'journal-content.txt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        console.log('[Export] Generating TXT file from current data');
        downloadTxtFile(state.data, state.journalNotes);
      }
    }
  };

  useEffect(() => {
    const resolvedPage = resolvePageFromPath(location.pathname);
    if (resolvedPage !== state.activePage) {
      dispatch({ type: 'SET_ACTIVE_PAGE', payload: resolvedPage });
    }
  }, [dispatch, location.pathname, state.activePage]);

  useEffect(() => {
    const updateMetadata = async () => {
    const searchParams = new URLSearchParams(location.search);
    const resolvedPage = resolvePageFromPath(location.pathname);
      const metadata = await resolvePageMetadata(resolvedPage, searchParams);

    applyPageMetadata(metadata);
    };

    updateMetadata();
  }, [location.pathname, location.search]);

  useEventListener('keydown', (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      setIsSearchOpen((previous) => !previous);
    }
  });

  const handleFilterChange = (filters: Partial<typeof state.filters>) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  };

  const handleClearFilters = () => {
    dispatch({ type: 'CLEAR_FILTERS' });
  };

  const handleViewChange = (view: ViewMode) => {
    dispatch({ type: 'SET_VIEW', payload: view });
  };

  useOnboardingFlow();

  const handleChatWithAI = useCallback(async () => {
    if (!state.data) {
      return;
    }

    setShowEirLoadOptionsModal(false);
    
    const THREAD_STORAGE_KEY = 'eir-chat-threads';
    const JOURNAL_NOTES_THREAD_ID = 'journal-notes';
      
    // Check for existing journal notes thread
    const threads: ChatThread[] = persistence.getJSON<ChatThread[]>(THREAD_STORAGE_KEY, []);
    
    const existingThread = threads.find((t: ChatThread) => t.journalNotesThreadId === JOURNAL_NOTES_THREAD_ID);
    
    let threadId: string;
    
    if (existingThread) {
      // Thread exists, navigate to it
      threadId = existingThread.id;
        } else {
      // Create new journal notes thread
      const newThread: ChatThread = {
        id: generateUniqueId('chat-thread'),
        title: 'Chat about your health',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messages: [],
        context: {
          clinicalMode: 'all',
          clinicalEntryIds: [],
          journalMode: 'all',
          journalNoteIds: [],
          nutritionMode: 'none',
          nutritionEntryIds: [],
          summaryMode: 'none',
          summaryEntryIds: [],
        },
        journalNotesThreadId: JOURNAL_NOTES_THREAD_ID,
        isNew: true,
      };
      
      threadId = newThread.id;
      threads.unshift(newThread);
      persistence.setJSON(THREAD_STORAGE_KEY, threads);
      
      // Store flag to send welcome message
      persistence.setJSON(`journal-notes-welcome-${threadId}`, {
        threadId,
        timestamp: new Date().toISOString(),
      });
      }
    
    // Navigate to chat page with the thread
    const chatPath = getPathForPage('eir-chat');
    navigate(`${chatPath}?threadId=${threadId}`);
  }, [state.data, navigate]);

  // Note: Removed auto-send greeting logic - now handled via thread creation in handleChatWithAI

  const handleAddJournalNote = (input: { content: string; wellbeingScore: number }) => {
    const noteContent = input.content.trim();
    if (!noteContent) {
      return;
    }

    const createdAt = new Date().toISOString();

    dispatch({
      type: 'ADD_JOURNAL_NOTE',
      payload: {
        id: generateUniqueId('note'),
        title: formatNoteTitle(createdAt),
        content: noteContent,
        createdAt,
        wellbeingScore: clampScore(input.wellbeingScore),
        source: 'manual'
      }
    });
  };

  const handleDeleteJournalNote = (noteId: string) => {
    dispatch({ type: 'DELETE_JOURNAL_NOTE', payload: noteId });
  };

  const handleToggleJournalNotes = () => {
    dispatch({ type: 'SET_SHOW_JOURNAL_NOTES', payload: !state.showJournalNotesInEHR });
  };

  const handleDeleteAllData = async () => {
    // Delete cloud backup if user is logged in
    if (authUser && supabaseAuth.isConfigured) {
      try {
        console.log('[App] Deleting EIR backup from cloud for user:', authUser.id);
        await cloudSyncService.deleteEirBackup(authUser.id, authSession);
        console.log('[App] Cloud backup deleted successfully');
      } catch (error) {
        console.warn('[App] Failed to delete cloud backup (continuing with local deletion):', error);
        // Continue with local deletion even if cloud deletion fails
      }
    }

    // Clear all localStorage data
    const storageKeys = [
      JOURNAL_STORAGE_KEY,
      CHAT_JOURNAL_STORAGE_KEY,
      EIR_FILE_STORAGE_KEY,
      EIR_DATA_STORAGE_KEY,
      HEALTH_ANALYSIS_STORAGE_KEY,
      HEALTH_ANALYSIS_META_STORAGE_KEY,
      CALENDAR_EVENTS_STORAGE_KEY,
      CALENDAR_SUGGESTIONS_STORAGE_KEY,
      'eir-nutrition-meal-log',
      'eir-health-videos',
      'eir-chat-threads',
      STORAGE_KEYS.HEALTH_ASSESSMENT_RESULTS,
      STORAGE_KEYS.HEALTH_ASSESSMENT_RESPONSES,
      STORAGE_KEYS.AI_ASSESSMENT_SUMMARY,
      STORAGE_KEYS.HEALTH_ACTIONS,
      'eir-user-profile'
    ];

    storageKeys.forEach(key => {
      persistence.removeItem(key);
    });

    // Reset all app state
    dispatch({ type: 'SET_DATA', payload: { metadata: {}, entries: [] } });
    dispatch({ type: 'SET_FILTERED_ENTRIES', payload: [] });
    dispatch({ type: 'SET_JOURNAL_NOTES', payload: [] });
    dispatch({ type: 'SET_CHAT_JOURNAL_ENTRIES', payload: [] });
    dispatch({ type: 'SET_LAST_CHAT_JOURNAL_SIZE', payload: 0 });
    dispatch({ type: 'RESET_HEALTH_ANALYSIS' });
    dispatch({ type: 'SET_CALENDAR_EVENTS', payload: [] });
    dispatch({ type: 'SET_CALENDAR_SUGGESTIONS', payload: [] });
    // Reset cloud backup load tracking so it won't try to reload after deletion
    hasLoadedCloudBackupRef.current = null;
    
    // Reload the page to ensure clean state
    window.location.reload();
  };

  const { activePage } = state;

  const handleSearchSelect = useCallback((result: SearchResult) => {
    const params = new URLSearchParams(result.searchParams ?? {});
    const search = params.toString();

    if (result.page !== activePage) {
      dispatch({ type: 'SET_ACTIVE_PAGE', payload: result.page });
    }

    navigate({
      pathname: getPathForPage(result.page),
      search: search ? `?${search}` : undefined
    });

    setIsSearchOpen(false);

    if (result.sectionSelector) {
      window.setTimeout(() => {
        const target = document.querySelector(result.sectionSelector!);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 450);
    }
  }, [activePage, dispatch, navigate]);

  const handleNavigate = useCallback(
    (page: AppPage) => {
      if (page !== activePage) {
        dispatch({ type: 'SET_ACTIVE_PAGE', payload: page });
      }
      navigate(getPathForPage(page));
    },
    [activePage, dispatch, navigate]
  );

  const handleHealthAnalysis = useCallback(async () => {
    if (!state.data) {
      return;
    }

    setShowEirLoadOptionsModal(false);
    dispatch({ type: 'SET_HEALTH_ANALYSIS_LOADING', payload: true });
    dispatch({ type: 'SET_HEALTH_ANALYSIS_ERROR', payload: null });

    try {
      const analysis = await apiService.analyzeHealth(state.data);
      dispatch({
        type: 'SET_HEALTH_ANALYSIS',
        payload: {
          analysis,
          generatedAt: analysis.generatedAt,
          model: analysis.model
        }
      });
      // Focus the clinical records page so the new analysis is visible
      handleNavigate('ehr');
    } catch (error) {
      console.error('Health analysis failed:', error);
      const message = getErrorMessage(error, 'Unable to complete health analysis.');
      dispatch({ type: 'SET_HEALTH_ANALYSIS_ERROR', payload: message });
    } finally {
      dispatch({ type: 'SET_HEALTH_ANALYSIS_LOADING', payload: false });
    }
  }, [state.data, dispatch, handleNavigate]);

  // Listen for health analysis and chat triggers
  useEventListener(TRIGGER_HEALTH_ANALYSIS_EVENT, () => {
    void handleHealthAnalysis();
  });
  useEventListener(TRIGGER_CHAT_WITH_AI_EVENT, () => {
    void handleChatWithAI();
  });

  const hasActiveFilters = Boolean(
    state.filters.category ||
    state.filters.provider ||
    state.filters.status ||
    state.filters.startDate ||
    state.filters.endDate ||
    state.filters.searchTerm
  );

  const remoteSessionActive = Boolean(
    remoteEhrSession && ['initializing', 'awaiting_bankid', 'authenticated'].includes(remoteEhrSession.status)
  );
  const remoteSessionBusy = remoteSessionActive || isStartingRemoteEhr || isFetchingRemoteEhrData;
  const canInitiateRemoteSession = !(remoteSessionActive || isStartingRemoteEhr || isFetchingRemoteEhrData);

  const timelineSubtitle = state.data
    ? hasActiveFilters
      ? 'Filtered clinical notes'
      : 'Clinical journal overview'
    : 'Load data to start exploring';

  const displayedJournalNotes = state.showJournalNotesInEHR ? state.journalNotes.slice(0, 6) : [];

  const isChatPage = state.activePage === 'eir-chat';

  return (
    <>
    <AppLayout
      data={state.data}
      filters={state.filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
      activePage={state.activePage}
      onNavigate={handleNavigate}
      onOpenSearch={() => setIsSearchOpen(true)}
      onOpenProfile={() => setIsProfileModalOpen(true)}
      betaFeaturesEnabled={betaFeaturesEnabled}
      hasData={!!state.data}
      onScrollToJournal={() => {
        const journalSection = document.querySelector('[data-journal-section]');
        if (journalSection) {
          journalSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }}
      onToggleJournalNotes={handleToggleJournalNotes}
      onChatWithAI={handleChatWithAI}
      onFetchFrom1177={handleRemoteEhrStart}
      isFetchingFrom1177={remoteSessionBusy}
      onRunHealthAnalysis={() => {
        void handleHealthAnalysis();
      }}
      error={remoteEhrError || state.error || null}
      onDismissError={handleDismissGlobalError}
      isChatPage={isChatPage}
    >
          <PageRoutes
            hasEhrData={!!state.data}
            entries={state.filteredEntries}
            currentView={state.currentView}
            onViewChange={handleViewChange}
            showJournalNotes={state.showJournalNotesInEHR}
            onToggleJournalNotes={handleToggleJournalNotes}
            displayedJournalNotes={displayedJournalNotes}
            journalNotes={state.journalNotes}
            onAddJournalNote={handleAddJournalNote}
            onDeleteJournalNote={handleDeleteJournalNote}
            onRunHealthAnalysis={() => {
              void handleHealthAnalysis();
            }}
            onLoadFile={() => triggerFileInput('.eir,.yaml,.yml', handleFileLoad)}
            onFetchFrom1177={handleRemoteEhrStart}
            isFetchingFrom1177={remoteSessionBusy}
          />
    </AppLayout>

      <GlobalSearch
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSelect={handleSearchSelect}
        entries={state.data?.entries ?? []}
        journalNotes={state.journalNotes}
        calendarEvents={state.calendarEvents}
      />

      <FileInput onFileLoad={handleFileLoad} />

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleSubmitFeedback}
      />

      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        onDeleteAllData={handleDeleteAllData}
        onLoadFile={() => triggerFileInput('.eir,.yaml,.yml', handleFileLoad)}
        onExport={handleExport}
        canExport={!!state.data}
        onAnalyze={() => {
          void handleHealthAnalysis();
        }}
        canAnalyze={!!state.data && !state.isLoading}
        analyzing={state.isHealthAnalysisLoading}
        onFetchRemoteEhr={handleRemoteEhrStart}
        canFetchRemoteEhr={canInitiateRemoteSession}
        fetchingRemoteEhr={remoteSessionBusy}
      />

      {showRemoteSessionIndicator && (
        <RemoteSessionIndicator
          status={remoteEhrSession?.status ?? null}
          isFetching={isFetchingRemoteEhrData}
          onOpenDetails={handleOpenRemoteSessionDetails}
        />
      )}

      {/* Only show modal on desktop - mobile uses deep link automatically */}
      {!isMobileDevice && (
        <BankIdModal
          isOpen={isBankIdModalOpen}
          status={remoteEhrSession?.status ?? 'cancelled'}
          qrImage={remoteEhrSession?.qrImage ?? undefined}
          onClose={() => {
            void handleCloseBankIdModal();
          }}
          onCancel={() => {
            void handleCancelRemoteEhrSession();
          }}
          onRefresh={remoteEhrSession ? () => {
            void handleRefreshBankIdQr();
          } : undefined}
          error={remoteEhrError || remoteEhrSession?.error || undefined}
          isFetchingData={isFetchingRemoteEhrData}
          sessionId={remoteEhrSession?.sessionId}
        />
      )}

      <EirLoadOptionsModal
        isOpen={showEirLoadOptionsModal && !!state.data}
        onChatWithAI={() => {
          void handleChatWithAI();
        }}
        onHealthAnalysis={() => {
          void handleHealthAnalysis();
        }}
        onCancel={() => setShowEirLoadOptionsModal(false)}
        isLoadingAnalysis={state.isHealthAnalysisLoading}
      />

      <OnboardingOverlay />
  </>
  );
};
